import React, { useState, useEffect } from 'react';
import './TelegramModal.css';
import { TELEGRAM_API_URL, TELEGRAM_BOT_USERNAME, OPBNB_SCAN_URL } from '../config/miningConstants';

const TelegramModal = ({ 
  account,
  userId, 
  userData, 
  incomeBreakdown, 
  miningData,
  onClose, 
  addToast 
}) => {
  const [telegramId, setTelegramId] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isLinked, setIsLinked] = useState(false);

  useEffect(() => {
    const savedId = localStorage.getItem('smartcore_telegram_id');
    if (savedId) {
      setTelegramId(savedId);
      setIsLinked(true);
    }
  }, []);

  const generateReportData = () => {
    const smartcoreData = {
      userId: userId || 'N/A',
      totalIncome: parseFloat(userData?.totalIncome || 0) / 1e18,
      totalClaimed: parseFloat(userData?.totalClaimed || 0) / 1e18,
      claimableAmount: parseFloat(userData?.claimableAmount || 0) / 1e18,
      directReferrals: userData?.totalDirectReferrals || 0,
      directReferralIncome: parseFloat(incomeBreakdown?.directReferralIncome || 0) / 1e18,
      matrixLevelIncome: parseFloat(incomeBreakdown?.matrixLevelIncome || 0) / 1e18,
      normalLevelIncome: parseFloat(incomeBreakdown?.normalLevelIncome || 0) / 1e18,
      poolIncome: parseFloat(incomeBreakdown?.poolIncome || 0) / 1e18,
      royaltyIncome: parseFloat(incomeBreakdown?.royaltyIncome || 0) / 1e18
    };

    const miningStats = {
      totalEnergy: parseFloat(miningData?.totalEnergy || 0) / 1e18,
      availableEnergy: parseFloat(miningData?.availableEnergy || 0) / 1e18,
      totalMined: parseFloat(miningData?.totalMined || 0) / 1e18,
      pendingBalance: parseFloat(miningData?.pendingBalance || 0) / 1e18,
      withdrawableBalance: parseFloat(miningData?.withdrawableBalance || 0) / 1e18,
      totalWithdrawn: parseFloat(miningData?.totalWithdrawn || 0) / 1e18,
      sessionCount: miningData?.sessionCount || 0,
      canMineToday: miningData?.canMineToday || false
    };

    return { smartcoreData, miningStats };
  };

  const formatReportMessage = () => {
    const { smartcoreData, miningStats } = generateReportData();
    const timestamp = new Date().toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    let message = `🌟 *SMARTCORE PORTFOLIO REPORT*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━━━\n\n`;
    
    message += `👤 *User ID:* \`${smartcoreData.userId}\`\n`;
    message += `📍 *Wallet:* \`${account?.slice(0, 6)}...${account?.slice(-4)}\`\n\n`;
    
    message += `💼 *SMARTCORE INVESTMENT*\n`;
    message += `├ 💰 Total Income: $${smartcoreData.totalIncome.toFixed(2)}\n`;
    message += `├ 💎 Available: $${smartcoreData.claimableAmount.toFixed(2)}\n`;
    message += `├ ✅ Claimed: $${smartcoreData.totalClaimed.toFixed(2)}\n`;
    message += `└ 👥 Referrals: ${smartcoreData.directReferrals}\n\n`;
    
    message += `📊 *INCOME BREAKDOWN*\n`;
    message += `├ Direct: $${smartcoreData.directReferralIncome.toFixed(2)}\n`;
    message += `├ Matrix: $${smartcoreData.matrixLevelIncome.toFixed(2)}\n`;
    message += `├ Level: $${smartcoreData.normalLevelIncome.toFixed(2)}\n`;
    message += `├ Pool: $${smartcoreData.poolIncome.toFixed(2)}\n`;
    message += `└ Royalty: $${smartcoreData.royaltyIncome.toFixed(2)}\n\n`;
    
    message += `⛏️ *CORE MINING*\n`;
    message += `├ 🔋 Total Energy: ${miningStats.totalEnergy.toFixed(2)} CORE\n`;
    message += `├ ⚡ Available: ${miningStats.availableEnergy.toFixed(2)} CORE\n`;
    message += `├ 💎 Total Mined: ${miningStats.totalMined.toFixed(2)} CORE\n`;
    message += `├ ⏳ Pending: ${miningStats.pendingBalance.toFixed(2)} CORE\n`;
    message += `├ 💰 Withdrawable: ${miningStats.withdrawableBalance.toFixed(2)} CORE\n`;
    message += `├ 📤 Withdrawn: ${miningStats.totalWithdrawn.toFixed(2)} CORE\n`;
    message += `├ 📊 Sessions: ${miningStats.sessionCount}\n`;
    message += `└ 🎯 Can Mine: ${miningStats.canMineToday ? '✅ Yes' : '❌ No'}\n\n`;
    
    message += `━━━━━━━━━━━━━━━━━━━━━━\n`;
    message += `📅 *Generated:* ${timestamp}\n`;
    message += `🔗 [View on opBNBscan](${OPBNB_SCAN_URL}/address/${account})\n\n`;
    message += `_Smartcore - The Future of Mining_`;

    return message;
  };

  const sendTelegramReport = async () => {
    if (!telegramId.trim()) {
      addToast('error', 'Please enter your Telegram Chat ID');
      return;
    }

    setIsSending(true);

    try {
      const message = formatReportMessage();

      const response = await fetch(TELEGRAM_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'send_message',
          chat_id: telegramId,
          message: message
        })
      });

      const data = await response.json();

      if (data.success) {
        addToast('success', '✅ Report sent to Telegram!');
        setIsLinked(true);
        localStorage.setItem('smartcore_telegram_id', telegramId);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      addToast('error', `❌ Failed: ${error.message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleUnlink = () => {
    localStorage.removeItem('smartcore_telegram_id');
    setTelegramId('');
    setIsLinked(false);
    addToast('success', '🔓 Telegram unlinked');
  };

  return (
    <div className="tg-overlay" onClick={onClose}>
      <div className="tg-modal" onClick={e => e.stopPropagation()}>
        <button className="tg-close" onClick={onClose}>✕</button>

        <div className="tg-header">
          <div className="tg-logo">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
            </svg>
          </div>
          <h2>Telegram Reports</h2>
          <p>Get instant portfolio notifications</p>
        </div>

        <div className="tg-steps">
          <div className="tg-step">
            <span className="step-num">1</span>
            <div className="step-info">
              <strong>Start the Bot</strong>
              <p>Search in Telegram:</p>
              <div className="bot-name" onClick={() => {
                navigator.clipboard.writeText(TELEGRAM_BOT_USERNAME);
                addToast('success', '📋 Copied!');
              }}>
                <code>{TELEGRAM_BOT_USERNAME}</code>
                <span>📋</span>
              </div>
            </div>
          </div>

          <div className="tg-step">
            <span className="step-num">2</span>
            <div className="step-info">
              <strong>Get Chat ID</strong>
              <p>Send <code>/start</code> to get your ID</p>
            </div>
          </div>

          <div className="tg-step">
            <span className="step-num">3</span>
            <div className="step-info">
              <strong>Enter Below</strong>
              <p>Paste your Chat ID</p>
            </div>
          </div>
        </div>

        <div className="tg-input-box">
          <label>Telegram Chat ID</label>
          <input
            type="text"
            placeholder="e.g., 123456789"
            value={telegramId}
            onChange={(e) => setTelegramId(e.target.value)}
          />
        </div>

        {isLinked && (
          <div className="tg-linked">
            <span className="linked-icon">✓</span>
            <span>Telegram linked!</span>
            <button onClick={handleUnlink}>Unlink</button>
          </div>
        )}

        <button
          className="tg-send-btn"
          onClick={sendTelegramReport}
          disabled={isSending || !telegramId.trim()}
        >
          {isSending ? (
            <><span className="spinner"></span> Sending...</>
          ) : isLinked ? (
            <>📤 Send Full Report</>
          ) : (
            <>🔗 Link & Send Report</>
          )}
        </button>

        <div className="tg-features">
          <p>📬 Auto-notifications:</p>
          <div className="features-list">
            <span>⚡ Investments</span>
            <span>💰 Income</span>
            <span>⛏️ Mining</span>
            <span>💎 Withdrawals</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TelegramModal;
